import { ApolloClient, ApolloQueryResult, NormalizedCacheObject } from '@apollo/client';
import Bugsnag from '@bugsnag/js';
import { handleQuery } from '@99designs/graph-utils';
import * as Types from '@99designs/graph-utils/types';
import { RelatedProductsDocument, RelatedProductsQuery } from '../graphql/ProductDetails.generated';
import { getProductGroupsConfigByKey } from '../lib/productGroups';
import { mapDataToRelatedProductTypeArray } from './mapDataToRelatedProductTypeArray';
import type { ProductGroupKey, RelatedProduct } from '../types';

export async function getRelatedProductsByProductGroupKey(
    client: ApolloClient<NormalizedCacheObject>,
    productGroupKey: ProductGroupKey
): Promise<RelatedProduct[] | null> {
    const { relatedProductsNames } = getProductGroupsConfigByKey(productGroupKey);
    if (!relatedProductsNames) {
        return [];
    }

    const inputQuery: Types.ProductsInput = {
        names: getProductGroupsConfigByKey(productGroupKey).relatedProductsNames,
    };
    const { data, error } = await fetchRelatedProductsOfProductGroup(client, inputQuery);

    if (error) {
        return null;
    }

    if (!data || !data.products) {
        Bugsnag.notify(
            new Error('Feature Product details - related products not found'),
            (event) => {
                event.context = `Related products not found for ${JSON.stringify(inputQuery)}`;
            }
        );
        return null;
    }

    if (!data.products.length) {
        Bugsnag.notify(
            new Error('Feature Product details - empty list of related products'),
            (event) => {
                event.context = `Related products returned empty for ${JSON.stringify(inputQuery)}`;
            }
        );
    }

    const relatedProducts = mapDataToRelatedProductTypeArray(data);

    return orderRelatedProducts(relatedProductsNames, relatedProducts);
}

function fetchRelatedProductsOfProductGroup(
    client: ApolloClient<NormalizedCacheObject>,
    inputQuery: Types.ProductsInput
): Promise<ApolloQueryResult<RelatedProductsQuery>> {
    return handleQuery<RelatedProductsQuery>(client, {
        query: RelatedProductsDocument,
        variables: {
            input: inputQuery,
        },
    });
}

function orderRelatedProducts(
    productNames: string[],
    relatedProducts: RelatedProduct[]
): RelatedProduct[] {
    const relatedProductsCopy = [...relatedProducts];
    const orderedProducts: RelatedProduct[] = [];

    productNames.forEach((name) => {
        const idx = relatedProductsCopy.findIndex((product) => product.name === name);
        if (idx < 0) {
            return;
        }

        const product = relatedProductsCopy.splice(idx, 1)[0];
        orderedProducts.push(product);
    });

    return orderedProducts;
}
